import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  PlatformAdminService,
  PlatformRestartOperationResponse,
  PlatformRestartTargetRecord,
  PlatformRestartTargetsCatalogResponse
} from './platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-platform-system-operations',
  imports: [AinPageComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule, DialogModule, CheckboxModule],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="t('platform.systemOperations.title')"
      [description]="t('platform.systemOperations.description')">
      @if (catalog()) {
        <section class="status-panel">
          <p>
            <strong>{{ t('platform.systemOperations.restartEnabled') }}:</strong>
            {{ catalog()!.restartEnabled ? t('common.yes') : t('common.no') }}
          </p>
          <p>
            <strong>{{ t('platform.systemOperations.restartMode') }}:</strong>
            {{ catalog()!.restartMode }}
          </p>
          <p>
            <strong>{{ t('platform.systemOperations.executorSupported') }}:</strong>
            {{ catalog()!.executorSupported ? t('common.yes') : t('common.no') }}
          </p>
        </section>

        <p-table [value]="catalog()!.targets">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('platform.systemOperations.target') }}</th>
              <th>{{ t('platform.systemOperations.displayName') }}</th>
              <th>{{ t('platform.systemOperations.configured') }}</th>
              <th>{{ t('platform.systemOperations.currentHost') }}</th>
              <th>{{ t('columns.actions') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><code>{{ row.key }}</code></td>
              <td>{{ row.displayName }}</td>
              <td>{{ row.configured ? t('common.yes') : t('common.no') }}</td>
              <td>{{ row.includesCurrentHost ? t('common.yes') : t('common.no') }}</td>
              <td>
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="danger"
                  [label]="t('platform.systemOperations.restartAction')"
                  [disabled]="!canRestart()"
                  (click)="openRestartDialog(row.key)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="toolbar-actions">
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('platform.systemOperations.restartBoth')"
            [disabled]="!canRestart()"
            (click)="openRestartDialog('both')"></button>
          <button pButton type="button" severity="secondary" [label]="t('platform.systemOperations.refresh')" (click)="loadCatalog()"></button>
        </div>
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }
      @if (disconnectGuidance()) {
        <p class="message warning">{{ disconnectGuidance() }}</p>
      }
    </ain-page>

    <p-dialog
      [header]="t('platform.systemOperations.confirmTitle')"
      [visible]="dialogOpen()"
      [modal]="true"
      [style]="{ width: '42rem', maxWidth: '96vw' }"
      (visibleChange)="dialogOpen.set($event)">
      <div class="dialog-body">
        <p>{{ t('platform.systemOperations.confirmIntro', { target: targetLabel(selectedTarget()) }) }}</p>
        @if (selectedTarget() === 'api' || selectedTarget() === 'both') {
          <p class="warning">{{ t('platform.systemOperations.apiDisconnectWarning') }}</p>
        }
        <label>
          <span>{{ t('platform.systemOperations.reason') }}</span>
          <textarea pInputText rows="4" [(ngModel)]="reason"></textarea>
        </label>
        <label class="confirm-row">
          <p-checkbox binary="true" [(ngModel)]="confirmed" />
          <span>{{ t('platform.systemOperations.confirmCheckbox') }}</span>
        </label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogOpen.set(false)"></button>
        <button
          pButton
          type="button"
          severity="danger"
          [label]="t('platform.systemOperations.submitRestart')"
          [disabled]="submitting() || !confirmed || !reason.trim()"
          (click)="submitRestart()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .status-panel { display: grid; gap: 6px; margin-bottom: 16px; color: #475569; }
    .toolbar-actions { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
    .dialog-body { display: grid; gap: 12px; }
    .dialog-body textarea { width: 100%; resize: vertical; }
    .confirm-row { display: flex; align-items: center; gap: 8px; }
    .message { margin-top: 12px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #166534; }
    .message.warning, .warning { color: #b45309; }
  `]
})
export class PlatformSystemOperationsComponent implements OnInit {
  private readonly platformAdmin = inject(PlatformAdminService);
  private readonly localization = inject(LocalizationService);

  readonly catalog = signal<PlatformRestartTargetsCatalogResponse | null>(null);
  readonly dialogOpen = signal(false);
  readonly submitting = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly disconnectGuidance = signal('');
  readonly selectedTarget = signal('api');

  reason = '';
  confirmed = false;

  readonly canRestart = computed(() => {
    const catalog = this.catalog();
    return !!catalog?.restartEnabled && catalog.executorSupported;
  });

  ngOnInit(): void {
    this.loadCatalog();
  }

  loadCatalog(): void {
    this.errorMessage.set('');
    this.platformAdmin.getSystemRestartTargets().subscribe({
      next: catalog => this.catalog.set(catalog),
      error: () => this.errorMessage.set(this.t('platform.systemOperations.loadFailed'))
    });
  }

  openRestartDialog(target: string): void {
    this.selectedTarget.set(target);
    this.reason = '';
    this.confirmed = false;
    this.successMessage.set('');
    this.disconnectGuidance.set('');
    this.dialogOpen.set(true);
  }

  submitRestart(): void {
    this.submitting.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.disconnectGuidance.set('');

    this.platformAdmin.requestSystemRestart({
      target: this.selectedTarget(),
      reason: this.reason.trim(),
      confirmation: this.confirmed
    }).subscribe({
      next: (response: PlatformRestartOperationResponse) => {
        this.submitting.set(false);
        this.dialogOpen.set(false);
        this.successMessage.set(`${response.message} (${response.status})`);
        if ((this.selectedTarget() === 'api' || this.selectedTarget() === 'both') && response.status === 'Requested') {
          this.disconnectGuidance.set(this.t('platform.systemOperations.reconnectGuidance'));
        }
      },
      error: () => {
        this.submitting.set(false);
        this.errorMessage.set(this.t('platform.systemOperations.restartFailed'));
      }
    });
  }

  targetLabel(target: string): string {
    return this.t(`platform.systemOperations.targets.${target}`);
  }

  t(key: string, params?: Record<string, string>): string {
    let value = this.localization.translate(key);
    if (!params) return value;
    for (const [name, replacement] of Object.entries(params))
      value = value.replace(`{{${name}}}`, replacement);
    return value;
  }
}
